import { useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import DashAvatar from '../../components/ui/DashAvatar'
import { EditProfilModal } from '../../components/livreur/modals/EditProfilModal'
import { usersAPI } from '../../api/users'
import { useAuthStore } from '../../store/authStore'
import type { Livreur } from '../../types'

const VEHICLE_LABELS: Record<string, string> = {
  moto: 'Moto', voiture: 'Voiture',
  camionnette: 'Camionnette', camion: 'Camion',
}

function ProfileRow({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      padding: '12px 0', borderTop: '1px solid var(--line)', fontSize: '.88rem',
    }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <b style={{ color: warn ? '#a06d12' : 'var(--navy-900)', textAlign: 'right', maxWidth: '60%' }}>{value}</b>
    </div>
  )
}

function Skeleton({ width = '100%', height = 16 }: { width?: string | number; height?: number }) {
  return (
    <div style={{
      width, height, borderRadius: 6,
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
    }} />
  )
}

export default function LivreurProfil() {
  const { user } = useAuthStore()
  const [showEdit, setShowEdit] = useState(false)

  const { data: profile, isLoading } = useQuery<Livreur>({
    queryKey: ['profile-me'],
    queryFn: () => usersAPI.getMe() as Promise<Livreur>,
  })

  const name    = profile?.name   ?? user?.name  ?? '—'
  const phone   = profile?.phone  ?? user?.phone ?? '—'
  const email   = profile?.email  ?? '—'
  const rating  = profile?.rating ?? 4.9
  const totalD  = profile?.totalDeliveries ?? 0
  const verified = profile?.isVerified ?? false

  const createdAt = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    : '—'

  const vehicle = profile?.vehicle
  const vType   = vehicle?.type  ? VEHICLE_LABELS[vehicle.type] ?? vehicle.type : '—'
  const vBrand  = vehicle?.brand ?? '—'
  const vModel  = vehicle?.model ?? '—'
  const vLabel  = vBrand !== '—' && vModel !== '—' ? `${vBrand} ${vModel}` : vBrand !== '—' ? vBrand : vModel
  const vPlate  = vehicle?.plate ?? '—'

  return (
    <>
      {showEdit && (
        <EditProfilModal
          profile={profile ?? null}
          onClose={() => setShowEdit(false)}
        />
      )}

      <div className="page-head">
        <div className="ph-titles">
          <h1>Mon profil</h1>
          <p>Informations personnelles, véhicule et documents.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => setShowEdit(true)}>
            Modifier le profil
          </button>
        </div>
      </div>

      <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 18 } as CSSProperties}>
        {/* Personal info card */}
        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Informations</h3>

          {isLoading ? (
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18 }}>
              <Skeleton width={64} height={64} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skeleton width="60%" />
                <Skeleton width="40%" />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18 }}>
              <DashAvatar name={name} size={64} />
              <div>
                <b style={{ color: 'var(--navy-900)', fontSize: '1.05rem', display: 'block' }}>{name}</b>
                <div style={{ color: 'var(--muted)', fontSize: '.85rem', marginTop: 2 }}>
                  Livreur · ID #{profile?.id ?? user?.id ?? '—'}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <span className="badge badge-green">★ {rating}</span>
                  {verified && <span className="badge badge-blue">Vérifié</span>}
                  {totalD > 0 && (
                    <span className="badge" style={{ background: 'var(--navy-50)', color: 'var(--navy-700)' }}>
                      {totalD} livraisons
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[120, 160, 100, 90].map((w, i) => <Skeleton key={i} width={`${w}px`} />)}
            </div>
          ) : (
            <>
              <ProfileRow label="Téléphone"     value={phone} />
              <ProfileRow label="Email"         value={email} />
              <ProfileRow label="Membre depuis" value={createdAt} />
              <ProfileRow
                label="Statut"
                value={profile?.isActive ? 'Actif' : 'Inactif'}
              />
            </>
          )}
        </div>

        {/* Vehicle & docs card */}
        <div className="card">
          <h3 style={{ marginBottom: 14 }}>Véhicule & documents</h3>

          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[100, 120, 90, 110].map((w, i) => <Skeleton key={i} width={`${w}px`} />)}
            </div>
          ) : (
            <>
              <ProfileRow label="Type"    value={vType} />
              <ProfileRow label="Modèle"  value={vLabel} />
              <ProfileRow label="Plaque"  value={vPlate} />
              <ProfileRow label="Permis"  value="Valide" />
            </>
          )}

          <div style={{
            marginTop: 18, padding: 14,
            background: 'var(--amber-50)', borderRadius: 10,
            fontSize: '.84rem', color: '#a06d12',
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <span>⚠️</span>
            <span>
              <b>Vérifiez vos documents régulièrement.</b>{' '}
              Une assurance ou un permis expiré peut suspendre votre compte.
            </span>
          </div>

          <button
            className="btn btn-ghost"
            style={{ marginTop: 14, width: '100%' }}
            onClick={() => setShowEdit(true)}
          >
            ✏️ Modifier le profil
          </button>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </>
  )
}
